import React, { useState, useRef, useEffect } from "react";
import { Box, Stepper, Step, StepLabel, Button, Typography, Paper, Alert } from "@mui/material";
import StepClientDetails from "./steps/StepClientDetails";
import StepBusinessStructure from "./steps/StepBusinessStructure";
import StepServicesPricing from "./steps/StepServicesPricing";
import StepReviewSign from "./steps/StepReviewSign";
import PdfTemplate from "./PdfTemplate";
import BusinessEngagementTemplate from "./BusinessEngagementTemplate";
import { PdfService } from "../../../services/pdf.service";

import dayjs from "dayjs";

const STEPS = ["Client Details", "Business Structure", "Services & Pricing", "Review & Sign", "PDF Generation"];

const QuotationWizard = ({ initialData, onSaveDraft, onFinish, onClose }) => {
    const [activeStep, setActiveStep] = useState(0);
    
    // Auto-generate reference number for new quotations
    const generateRefNumber = () => {
        const datePart = dayjs().format("YYYYMMDD");
        const randomPart = Math.random().toString(36).substring(2, 6).toUpperCase();
        return `QTN-${datePart}-${randomPart}`;
    };

    const [data, setData] = useState(() => {
        if (initialData) return initialData;
        
        return {
            id: generateRefNumber(),
            client: {},
            businessStructure: {},
            services: { 
                bookkeeping: { selected: [], others: [] }, 
                taxation: { selected: [], others: [] }, 
                structuring: { selected: [], others: [] } 
            },
            totals: { subtotal: 0, grandTotal: 0 },
            engagementDetails: { accountingSoftwareFee: false, accountingSoftwareFeePrice: 30, startDate: '', endDate: '' },
            firmSignOff: { signedDate: dayjs().format("YYYY-MM-DD") },
            acknowledgement: { clientSignedDate: dayjs().format("YYYY-MM-DD") },
            createdAt: new Date().toISOString()
        };
    });
    
    // PDF Ref
    const pdfRef = useRef(null);
    const [generating, setGenerating] = useState(false);

    // Calculate totals automatically when services change
    useEffect(() => {
        let subtotal = 0;
        
        const sumGroup = (group) => {
            if (group?.selected) {
                subtotal += group.selected.reduce((acc, curr) => acc + (Number(curr.price) || 0), 0);
            }
            if (group?.others) {
                 subtotal += group.others.reduce((acc, curr) => acc + (Number(curr.price) || 0), 0);
            }
            // Fallback for old data if any
            if (group?.other?.price) {
                subtotal += Number(group.other.price);
            }
        };

        sumGroup(data.services.bookkeeping);
        sumGroup(data.services.taxation);
        sumGroup(data.services.structuring);

        const gst = subtotal * 0.10;
        const grandTotal = subtotal + gst;

        setData(prev => ({
            ...prev,
            totals: { subtotal, gst, grandTotal }
        }));
    }, [data.services]);
    
    // Validation
    const isStepValid = () => {
        switch (activeStep) {
            case 0: // Client
                return data.client?.clientName && data.client?.clientEmail;
            case 1: // Structure
                if (!data.businessStructure?.pdfTitle) return false;
                if (!data.businessStructure?.structure) return false;
                if (data.businessStructure.structure === 'Other' && !data.businessStructure.businessStructureOther) return false;
                return true;
            case 2: // Services
                // Must have at least one service selected OR other entered, AND all selected must have price > 0
                const groups = ['bookkeeping', 'taxation', 'structuring'];
                let hasSelection = false;
                for (const group of groups) {
                    const gData = data.services[group];
                    // Check standard selected items
                    if (gData?.selected?.length > 0) {
                        hasSelection = true;
                        if (gData.selected.some(item => item.price === "" || item.price === undefined || item.price === null || Number(item.price) < 0)) return false;
                    }
                    // Check custom items
                    if (gData?.others?.length > 0) {
                        hasSelection = true;
                        if (gData.others.some(item => item.price === "" || item.price === undefined || item.price === null || Number(item.price) < 0)) return false;
                    }
                    // Legacy check (just in case)
                    if (gData?.other?.text) {
                        hasSelection = true;
                        if (gData.other.price === "" || gData.other.price === undefined || gData.other.price === null || Number(gData.other.price) < 0) return false;
                    }
                }
                return hasSelection;
            case 3: // Review Sign
                // Firm sign off required (defaults are there) but Client sign off is technically optional for Draft, 
                // but for PDF generation (Next Step) we probably want it? 
                // Prompt says "Client acknowledgement (editable defaults)". 
                // Let's assume Valid if basically filled.
                return true;
            default:
                return true;
        }
    };

    const handleNext = async () => {
        if (activeStep === STEPS.length - 2) { // Moving to Final Step (PDF)
             // Ensure data is saved/finalized
        }
        setActiveStep(prev => prev + 1);
    };

    const handleBack = () => {
        setActiveStep(prev => prev - 1);
    };

    const handleDownload = async () => {
        if (!pdfRef.current) return;
        setGenerating(true);
        try {
            const isEngagement = data.businessStructure?.pdfTitle === 'Business Engagement';
            const filename = isEngagement
                ? `BusinessEngagement-${data.client.clientName}.pdf`
                : `Quotation-${data.client.clientName}.pdf`;
            await PdfService.downloadPdf(pdfRef.current, filename);
            onFinish(data, true); // True = downloaded
        } catch (e) {
            console.error(e);
            alert("Failed to generate PDF");
        } finally {
            setGenerating(false);
        }
    };
    
    const handleSaveAndClose = () => {
        onSaveDraft(data);
    };

    return (
        <Box sx={{ p: 2 }}>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
                {STEPS.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* Step Content */}
            <Box sx={{ mb: 4, minHeight: '300px' }}>
                {activeStep === 0 && (
                    <StepClientDetails data={data.client} onChange={(val) => setData({ ...data, client: val })} />
                )}
                {activeStep === 1 && (
                    <StepBusinessStructure data={data.businessStructure} onChange={(val) => setData({ ...data, businessStructure: val })} />
                )}
                {activeStep === 2 && (
                    <StepServicesPricing data={data.services} onChange={(val) => setData({ ...data, services: val })} />
                )}
                {activeStep === 3 && (
                    <StepReviewSign data={data} onChange={setData} />
                )}
                {activeStep === 4 && (
                     <Box sx={{ textAlign: 'center' }}>
                         <Alert severity="success" sx={{ mb: 3 }}>
                             Quotation is ready to be generated!
                         </Alert>
                         <Typography variant="body1" gutterBottom sx={{ mb: 3 }}>
                             Please review the PDF preview below. If everything looks correct, click 'Download PDF'.
                         </Typography>
                         
                         {/* Hidden PDF Container for Generation check transparency */}
                         <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <Paper elevation={3} sx={{ p: 0, width: '210mm', minHeight: '297mm', overflow: 'hidden' }}>
                                {data.businessStructure?.pdfTitle === 'Business Engagement'
                                    ? <BusinessEngagementTemplate ref={pdfRef} quotation={data} />
                                    : <PdfTemplate ref={pdfRef} quotation={data} />
                                }
                            </Paper>
                         </Box>
                     </Box>
                )}
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                <Button onClick={onClose} color="inherit">
                    Close
                </Button>
                
                <Box>
                    {activeStep !== 4 && (
                        <Button onClick={handleSaveAndClose} sx={{ mr: 2 }}>
                            Save Draft
                        </Button>
                    )}
                    
                    {activeStep > 0 && (
                        <Button onClick={handleBack} sx={{ mr: 1 }} disabled={generating}>
                            Back
                        </Button>
                    )}
                    
                    {activeStep < STEPS.length - 1 ? (
                        <Button 
                            variant="contained" 
                            onClick={handleNext} 
                            disabled={!isStepValid()}
                        >
                            Next
                        </Button>
                    ) : (
                        <Button 
                            variant="contained" 
                            color="success" 
                            onClick={handleDownload}
                            disabled={generating}
                        >
                            {generating ? "Generating..." : "Download PDF"}
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default QuotationWizard;
